import { Countries } from './countries';

/**
 * Where a project's data lives.
 *
 * `VITE_REG` is the environment's region list. Its VALUES are the AWS regions
 * this environment can create a project in, and its KEYS pin a visitor country
 * to one of them. A region belongs in there only once its layer/admin/record
 * stacks are deployed AND merged into the backend's resource list, otherwise
 * create-service answers `Region "<code>" is not supported`.
 *
 * The catalogue below names and places those regions, so the picker can show a
 * city instead of a region code and "closest to you" is measured against the
 * region's own city rather than the centroid of whichever country its pin
 * happens to name (a US pin sits in Kansas, us-west-2 is in Oregon). Every code
 * the backend's `short_region()` accepts is listed, so putting a region on offer
 * is a VITE_REG edit and nothing else.
 *
 * A project cannot be moved after creation: the region is encoded in the first
 * four characters of the service id.
 */
export type ServiceRegion = {
	/** AWS region code, the value create-service takes. */
	region: string;
	/** Where it is, in the words someone picks a region with. */
	label: string;
	/** The region's city. Absent for a code missing from the catalogue, which
	 * then ranks last instead of being placed at 0,0 off the coast of Africa. */
	latitude?: number;
	longitude?: number;
};

const REGION_CATALOG: ServiceRegion[] = [
	{ region: 'us-east-1', label: 'N. Virginia, United States', latitude: 39.0438, longitude: -77.4874 },
	{ region: 'us-east-2', label: 'Ohio, United States', latitude: 40.4173, longitude: -82.9071 },
	{ region: 'us-west-1', label: 'N. California, United States', latitude: 37.7749, longitude: -122.4194 },
	{ region: 'us-west-2', label: 'Oregon, United States', latitude: 45.8399, longitude: -119.7006 },
	{ region: 'ca-central-1', label: 'Montreal, Canada', latitude: 45.5019, longitude: -73.5674 },
	{ region: 'sa-east-1', label: 'Sao Paulo, Brazil', latitude: -23.5505, longitude: -46.6333 },
	{ region: 'eu-west-1', label: 'Dublin, Ireland', latitude: 53.3498, longitude: -6.2603 },
	{ region: 'eu-west-2', label: 'London, United Kingdom', latitude: 51.5074, longitude: -0.1278 },
	{ region: 'eu-west-3', label: 'Paris, France', latitude: 48.8566, longitude: 2.3522 },
	{ region: 'eu-central-1', label: 'Frankfurt, Germany', latitude: 50.1109, longitude: 8.6821 },
	{ region: 'eu-south-1', label: 'Milan, Italy', latitude: 45.4642, longitude: 9.19 },
	{ region: 'eu-north-1', label: 'Stockholm, Sweden', latitude: 59.3293, longitude: 18.0686 },
	{ region: 'me-south-1', label: 'Manama, Bahrain', latitude: 26.0667, longitude: 50.5577 },
	{ region: 'af-south-1', label: 'Cape Town, South Africa', latitude: -33.9249, longitude: 18.4241 },
	{ region: 'ap-south-1', label: 'Mumbai, India', latitude: 19.076, longitude: 72.8777 },
	{ region: 'ap-southeast-1', label: 'Singapore', latitude: 1.3521, longitude: 103.8198 },
	{ region: 'ap-southeast-2', label: 'Sydney, Australia', latitude: -33.8688, longitude: 151.2093 },
	{ region: 'ap-southeast-3', label: 'Jakarta, Indonesia', latitude: -6.2088, longitude: 106.8456 },
	{ region: 'ap-east-1', label: 'Hong Kong', latitude: 22.3193, longitude: 114.1694 },
	{ region: 'ap-northeast-1', label: 'Tokyo, Japan', latitude: 35.6895, longitude: 139.6917 },
	{ region: 'ap-northeast-2', label: 'Seoul, South Korea', latitude: 37.5665, longitude: 126.978 },
	{ region: 'ap-northeast-3', label: 'Osaka, Japan', latitude: 34.6937, longitude: 135.5023 },
];

const CATALOG_BY_REGION: { [region: string]: ServiceRegion } = REGION_CATALOG.reduce(
	(acc, r) => {
		acc[r.region] = r;
		return acc;
	},
	{} as { [region: string]: ServiceRegion },
);

/** `{ "<visitor country>": "<aws region>" }` straight out of the environment. */
const regionPins: { [country: string]: string } = (() => {
	try {
		return JSON.parse(import.meta.env.VITE_REG) || {};
	} catch (err) {
		console.error('VITE_REG is not valid JSON:', err);
		return {};
	}
})();

/**
 * The regions this environment offers, in VITE_REG order, deduped (two
 * countries may well pin the same region). A region with no catalogue entry is
 * still offered -- a rollout that adds one to VITE_REG before this table works
 * must not silently drop it -- it just shows its raw code and ranks last.
 */
export const availableRegions: ServiceRegion[] = (() => {
	let seen: { [region: string]: true } = {};
	let out: ServiceRegion[] = [];

	for (let country in regionPins) {
		let code = regionPins[country];
		if (!code || seen[code]) continue;
		seen[code] = true;
		out.push(CATALOG_BY_REGION[code] || { region: code, label: code });
	}

	return out;
})();

export function isAvailableRegion(region: string): boolean {
	return !!region && availableRegions.some((r) => r.region === region);
}

/** Human name for a region code, falling back to the code itself. */
export function regionLabel(region: string): string {
	return CATALOG_BY_REGION[region]?.label || region || '';
}

/** Great-circle distance in metres. */
function distance(
	from: { latitude: number; longitude: number },
	to: { latitude: number; longitude: number },
): number {
	const R = 6371e3; // metres
	const φ1 = (from.latitude * Math.PI) / 180; // φ, λ in radians
	const φ2 = (to.latitude * Math.PI) / 180;
	const Δφ = ((to.latitude - from.latitude) * Math.PI) / 180;
	const Δλ = ((to.longitude - from.longitude) * Math.PI) / 180;

	const a =
		Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
		Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

	return R * c;
}

/**
 * Whether the visitor can actually be placed, i.e. whether the ranking below is
 * a real "closest" order rather than plain VITE_REG order. A pin for their own
 * country counts: that country was routed on purpose.
 */
export function isLocatedLocale(locale?: string | null): boolean {
	if (!locale) return false;
	if (regionPins[locale] && isAvailableRegion(regionPins[locale])) return true;
	let here = Countries[locale];
	return !!here && typeof here.latitude === 'number' && typeof here.longitude === 'number';
}

/**
 * The offered regions, nearest the visitor first.
 *
 * A pin for the visitor's own country wins outright: it is an explicit routing
 * decision for that country, not a guess from coordinates. Everything else is
 * ranked by how far the region's city is from the centre of the visitor's
 * country -- the only location a browser gives us without asking. An unknown
 * locale (or a region missing from the catalogue) keeps VITE_REG order rather
 * than inventing a distance.
 */
export function regionsForLocale(locale?: string | null): ServiceRegion[] {
	// Seven entries in the country table carry no coordinates (Kosovo, South
	// Sudan, Curacao and the like); reading them would turn every distance into
	// NaN, so treat them as an unknown locale.
	let known = locale ? Countries[locale] : null;
	let here =
		known && typeof known.latitude === 'number' && typeof known.longitude === 'number'
			? known
			: null;
	let ranked = availableRegions.slice();

	if (here) {
		let order = new Map<string, number>();
		ranked.forEach((r, i) => {
			order.set(
				r.region,
				r.latitude === undefined || r.longitude === undefined
					? Number.POSITIVE_INFINITY
					: distance(here, { latitude: r.latitude, longitude: r.longitude }),
			);
			// Ties (and unplaceable regions) fall back to VITE_REG order.
			order.set(r.region, (order.get(r.region) as number) + i * 1e-6);
		});
		ranked.sort(
			(a, b) => (order.get(a.region) as number) - (order.get(b.region) as number),
		);
	}

	let pinned = locale ? regionPins[locale] : '';
	if (pinned && isAvailableRegion(pinned)) {
		ranked = [
			ranked.find((r) => r.region === pinned) as ServiceRegion,
			...ranked.filter((r) => r.region !== pinned),
		];
	}

	return ranked;
}

/**
 * The region a project defaults to: the closest one to the visitor. Empty only
 * when the environment offers no region at all, which create-service rejects.
 */
export function closestRegion(locale?: string | null): string {
	return regionsForLocale(locale)[0]?.region || '';
}

/**
 * The region a create request should carry: what the user picked, as long as it
 * is one this environment actually offers, else the closest one. Never trust a
 * value off the wire (a stale form, a hand-edited option) to reach the backend.
 */
export function resolveRegion(picked?: string | null, locale?: string | null): string {
	return picked && isAvailableRegion(picked) ? picked : closestRegion(locale);
}
